import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account, Payee } from "model";

/**
 * @class ScheduledWire
 * @classdesc Class definition for scheduled wire records.
 * @param {OBJECT} record - record from the model to initialize the class
 * @extends BCMSRecord
 * @property {STRING} _id - The scheduled wire ID.
 * @property {STRING} _referenceNumber - The wire reference number.
 * @property {PAYEE} _payee - The wire payee.
 * @property {BOOLEAN} _notifyPayee - Whether the payee should be notified.
 * @property {ACCOUNT} _fundingAccount - The funding account.
 * @property {BCMSCurrency} _amount - The wire amount.
 * @property {STRING} _status - The wire status.
 * @property {STRING} _statusDescription - The wire status description.
 * @property {BCMSDate} _date - The wire date.
 * @property {BCMSDate} _futureWireDate - The future wire date.
 * @property {STRING} _memo - The wire memo.
 * @property {STRING} _beneficiaryReference - The beneficiary reference.
 * @property {STRING} _messageToBeneficiary1 - The first beneficiary message.
 * @property {STRING} _messageToBeneficiary2 - The second beneficiary message.
 * @property {STRING} _messageToBeneficiary3 - The third beneficiary message.
 * @property {STRING} _messageToBeneficiary4 - The fourth beneficiary message.
 * @property {STRING} _reasonForPayment - The payment reason.
 * @property {BCMSDate} _recurringEndByDate - The recurring end date.
 * @property {BCMSDate} _recurringWireDate - The next recurring wire date.
 * @property {STRING} _recurringNumberOfTransfers - The recurring transfer count.
 * @property {STRING} _schedulingOption - The scheduling option.
 * @property {STRING} _frequency - The recurrence frequency.
 * @property {STRING} _frequencyType - The recurrence end condition.
 */
export default class ScheduledWire extends BCMSRecord {
  _id;
  _referenceNumber;
  _payee;
  _notifyPayee;
  _fundingAccount;
  _amount;
  _status;
  _statusDescription;
  _date;
  _futureWireDate;
  _memo;
  _beneficiaryReference;
  _messageToBeneficiary1;
  _messageToBeneficiary2;
  _messageToBeneficiary3;
  _messageToBeneficiary4;
  _reasonForPayment;
  _recurringEndByDate;
  _recurringWireDate;
  _recurringNumberOfTransfers;
  _schedulingOption;
  _frequency;
  _frequencyType;
  _payeeName;

  constructor(record) {
    super(record)
  }
}

ScheduledWire.define({
  "recordType": "scheduledWire",
  "recordIDProp": "id",
  "className": "ScheduledWire",
  "recordProperties": [
      /** 
       * @name id
       * @instance
       * @property get {NUMBER} - gets the id 
       * @property set {NUMBER} - sets the id 
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("id","_id"),
      /** 
       * @name referenceNumber
       * @instance
       * @property get {STRING} - gets the reference number
       * @property set {STRING} - sets the reference number
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("referenceNumber","_referenceNumber"),
      /** 
       * @name payee
       * @instance
       * @property get {STRING} - gets the payee
       * @property set {STRING} - sets the payee
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToClass("payee","_payee", () => Payee),
      /** 
       * @name notifyPayee
       * @instance
       * @property get {BOOLEAN} - gets the notifyPayee value
       * @property set {BOOLEAN} - sets the notifyPayee value
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToBoolean("notifyPayee", "_notifyPayee"),
       /** 
       * @name fundingAccount
       * @instance
       * @property get {STRING} - gets the funding account
       * @property set {STRING} - sets the funding account
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToClass("fundingAccount","_fundingAccount", () => Account),
       /** 
       * @name amount
       * @instance
       * @property get {STRING} - gets the amount
       * @property set {STRING} - sets the amount
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToCurrency("amount","_amount"),
       /** 
       * @name statusDescription
       * @instance
       * @property get {STRING} - gets the statusDescription
       * @property set {STRING} - sets the statusDescription
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("statusDescription","_statusDescription"),
       /** 
       * @name date
       * @instance
       * @property get {STRING} - gets the date
       * @property set {STRING} - sets the date
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToDate("date","_date"),
      /** 
       * @name futureWireDate
       * @instance
       * @property get {STRING} - gets the futureWireDate
       * @property set {STRING} - sets the futureWireDate
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToDate("futureWireDate","_futureWireDate"),
      /** 
       * @name memo
       * @instance
       * @property get {STRING} - gets the memo
       * @property set {STRING} - sets the memo
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("memo","_memo"),
      /** 
       * @name beneficiaryReference
       * @instance
       * @property get {STRING} - gets the beneficiaryReference
       * @property set {STRING} - sets the beneficiaryReference
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("beneficiaryReference","_beneficiaryReference"),
      /** 
       * @name messageToBeneficiary1
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary1
       * @property set {STRING} - sets the messageToBeneficiary1
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary1","_messageToBeneficiary1"),
      /** 
       * @name messageToBeneficiary2
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary2
       * @property set {STRING} - sets the messageToBeneficiary2
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary2","_messageToBeneficiary2"),
      /** 
       * @name messageToBeneficiary3
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary3
       * @property set {STRING} - sets the messageToBeneficiary3
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary3","_messageToBeneficiary3"),
      /** 
       * @name messageToBeneficiary4
       * @instance
       * @property get {STRING} - gets the messageToBeneficiary4
       * @property set {STRING} - sets the messageToBeneficiary4
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("messageToBeneficiary4","_messageToBeneficiary4"),
      /** 
       * @name reasonForPayment
       * @instance
       * @property get {STRING} - gets the reasonForPayment
       * @property set {STRING} - sets the reasonForPayment
       * @memberof ScheduledWire
       */
            BCMSRecordProperty.mapToString("reasonForPayment","_reasonForPayment"),
      /** 
       * @name recurringEndByDate
       * @instance
       * @property get {STRING} - gets the recurringEndByDate
       * @property set {STRING} - sets the recurringEndByDate
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToDate("recurringEndByDate","_recurringEndByDate"),
      /** 
       * @name recurringWireDate
       * @instance
       * @property get {STRING} - gets the recurringWireDate
       * @property set {STRING} - sets the recurringWireDate
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToDate("recurringWireDate","_recurringWireDate"),
      /** 
       * @name recurringNumberOfTransfers
       * @instance
       * @property get {STRING} - gets the recurringNumberOfTransfers
       * @property set {STRING} - sets the recurringNumberOfTransfers
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("recurringNumberOfTransfers","_recurringNumberOfTransfers"),
      /** 
       * @name schedulingOption
       * @instance
       * @property get {STRING} - gets the schedulingOption
       * @property set {STRING} - sets the schedulingOption
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("schedulingOption","_schedulingOption"),
      /** 
       * @name frequency
       * @instance
       * @property get {STRING} - gets the frequency
       * @property set {STRING} - sets the frequency
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("frequency","_frequency"),
      /** 
       * @name frequencyType
       * @instance
       * @property get {STRING} - gets the frequencyType
       * @property set {STRING} - sets the frequencyType
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("frequencyType","_frequencyType"),
      /** 
       * @name payeeName
       * @instance
       * @property get {STRING} - gets the payeeName
       * @property set {STRING} - sets the payeeName
       * @memberof ScheduledWire
       */
      BCMSRecordProperty.mapToString("payeeName","_payeeName"),
  ]
})
